import ColumnFilter from "components/ColumnFilter";
import useDetectFilterChange from "hooks/useDetectFilterChange";
import useUrlFilters from "hooks/useUrlFilters";
import {
  compact,
  forEach,
  isEmpty,
  isFunction,
  isObject,
  map,
  noop,
  omit,
  omitBy,
  uniqBy,
} from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CancellableAPIRequest } from "restAPI";
import FindingAPI from "restAPI/FindingAPI";
import { FilterInfo } from "types/pagination";
import {
  CountType,
  OptionType,
  OptionTypeV3,
  differentiateOptionsWithCount,
} from "utils/columnFilterUtil";
import { formatNumber } from "utils/numberUtil";
import Sorters, { SorterFunction } from "utils/sorters";

export default function ACContextualFilterDropdown({
  componentProps,
  name,
  api,
  skipSearchAPICall,
  sortOptions = true,
  showCount = true,
  useFilterAPIV3 = true,
  ...restProps
}: {
  name: string;
  componentProps: object;
  api?: ({
    name,
    filters,
    searchText,
  }: {
    name: string;
    searchText: string;
    filters: FilterInfo;
  }) => CancellableAPIRequest<{
    options: OptionType[];
    counts: CountType[];
  }>;
  skipSearchAPICall?: boolean;
  sortOptions: boolean | SorterFunction<OptionType>;
  showCount?: boolean;
  useFilterAPIV3?: boolean;
}) {
  const [options, setOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState(true);
  const cancelRef = useRef(noop);
  const sorterRef = useRef(sortOptions);

  const filters = useUrlFilters();

  // @ts-expect-error TODO fixe type
  const filtersFromProps = restProps.filterInfo;

  // filters - filters from URL
  // filtersFromProps - filters from the filter bar component, both are different in case of widgets and both are same for dashboard/findings/etc
  // filters from dashboard/url should take priority
  const finalFilters = useMemo(
    () =>
      omit(
        {
          ...filtersFromProps,
          ...filters,
        },
        name,
      ),
    [filtersFromProps, filters, name],
  );
  const actualAppliedFilters = useDetectFilterChange(finalFilters);

  useEffect(() => setOptions([]), [actualAppliedFilters]);

  const fetchValues = useCallback(
    (searchText: string) => {
      if (skipSearchAPICall && searchText.length) {
        return;
      }
      if (cancelRef.current) {
        cancelRef.current();
      }
      setLoading(true);
      let cancel = noop;
      const timeout = setTimeout(() => {
        let request;
        const _filters = actualAppliedFilters;
        if (!api && useFilterAPIV3) {
          request = FindingAPI.getFilterValuesV3(
            name === "team" ? "teamId" : name,
            searchText,
            omitBy(_filters, isEmpty),
          );
          request.promise
            .then((values) => {
              const options: Array<OptionType & { count: number }> = map(
                values.data.filtersCountPairValues,
                (count: OptionTypeV3 | number, filter) => {
                  if (isObject(count)) {
                    return {
                      id: count.id,
                      name: count.name,
                      count: count.count ?? 0,
                      secondaryText: formatNumber(count.count ?? 0),
                    };
                  }
                  return {
                    id: filter,
                    name: filter,
                    count: count ?? 0,
                    secondaryText: formatNumber(count ?? 0),
                  };
                },
              );
              let optionsWithCount: Array<OptionType & { count: number }> = [],
                optionsWithoutCount: Array<OptionType & { count: number }> = [];
              forEach(options, (option) => {
                if (option.count > 0) {
                  optionsWithCount.push(option);
                } else {
                  optionsWithoutCount.push(option);
                }
              });
              let sorter: SorterFunction<OptionType> | undefined;
              if (sorterRef.current) {
                sorter = isFunction(sorterRef.current)
                  ? sorterRef.current
                  : Sorters.STRING("name");
                optionsWithCount = optionsWithCount.sort(sorter);
                optionsWithoutCount = optionsWithoutCount.sort(sorter);
              }
              setOptions((prev) =>
                uniqBy(
                  [...prev, ...optionsWithCount, ...optionsWithoutCount],
                  "id",
                ),
              );
            })
            .finally(() => setLoading(false));
          cancel = request.cancel;
        } else {
          if (api) {
            request = api({ name, searchText, filters: _filters });
          } else {
            const allValuesRequest = FindingAPI.getSingleFilterValues(
              name,
              searchText,
              {},
            );
            const countRequest = FindingAPI.getStatsCount({
              aggField: name,
              secAggField: "severity",
              filters: omitBy(_filters, isEmpty),
              // includeMissing: true
            });
            const combinedPromise = Promise.all([
              allValuesRequest.promise,
              countRequest.promise,
            ]).then(([values, counts]: [string[], CountType[]]) => {
              return {
                options: values.map(
                  (name) => ({ name, id: name }) as OptionType,
                ),
                counts,
              };
            });
            request = {
              promise: combinedPromise,
              cancel: () => {
                allValuesRequest.cancel();
                countRequest.cancel();
              },
            };
          }
          cancel = request.cancel;
          request.promise
            .then(({ options, counts }) => {
              let { optionsWithCount, optionsWithoutCount } = showCount
                ? differentiateOptionsWithCount(options, counts)
                : { optionsWithCount: options, optionsWithoutCount: [] };
              if (sorterRef.current) {
                const sortOptions = sorterRef.current;
                const sorter = isFunction(sortOptions)
                  ? sortOptions
                  : Sorters.STRING("name");
                optionsWithCount = optionsWithCount.sort(sorter);
                optionsWithoutCount = optionsWithoutCount.sort(sorter);
              }
              setOptions((prev) =>
                uniqBy(
                  compact([
                    ...prev,
                    ...optionsWithCount,
                    ...optionsWithoutCount,
                  ]),
                  "id",
                ),
              );
            })
            .finally(() => setLoading(false));
        }
      }, 500);

      cancelRef.current = () => {
        cancel();
        clearTimeout(timeout);
      };
    },
    [
      skipSearchAPICall,
      actualAppliedFilters,
      useFilterAPIV3,
      api,
      name,
      showCount,
    ],
  );

  return (
    <ColumnFilter
      {...restProps}
      {...componentProps}
      filters={options}
      onSearchChange={fetchValues}
      loading={loading}
      refreshOnOpen
      sortOptions={false}
    />
  );
}
